/* eslint-disable @typescript-eslint/no-unused-vars */
import queryString from 'query-string'
import { useState } from 'react'
import useSWR, { mutate } from 'swr'

import { stringifyParams } from 'utils/stringFormatting'

import * as T from 'types'

import { api } from '../api'
import { fetchWithToken } from '../fetchers'
import { getDataFromResponse } from './utils/dataHelpers'
import { getFetchErrorMessage, getUpdateErrorMessage } from './utils/errorMessageHelpers'

export const useIvrSession = (id: string = '') => {
  const apiEndpoint: string | null = id ? `/ivrSession/${id}` : null

  const response = useSWR<T.IIvrSession | T.IServerError>(apiEndpoint, fetchWithToken)

  let ivrSession: T.IIvrSession | undefined

  if (response.data) ivrSession = response.data as T.IIvrSession

  const [errors, setErrors] = useState<{ [key: string]: T.IServerError }>({})
  const error = getFetchErrorMessage(response, 'ivrSession')

  const update = async (updates: { [key: string]: T.ValueOf<T.IIvrSession> }) => {
    if (!ivrSession || !id) {
      return setErrors({ ...errors, update: { message: 'There is no IVR session' } })
    }

    // we POST the API and update the UI with the data that comes back on success
    const updateResponse = await api.updateIvrSession(ivrSession._id, updates)
    const updatedIvrSession = getDataFromResponse<T.IIvrSession>(updateResponse)

    const userReadableError = 'failed to update'
    const updateError = getUpdateErrorMessage(updateResponse, { userReadableError })

    if (updateError.message) return setErrors({ ...errors, update: updateError })

    await mutate(apiEndpoint, updatedIvrSession, false)
  }

  const updateSearch = async (
    ivrSessionId: string,
    updates: { [key: string]: T.ValueOf<T.IIvrSession> },
    searchQueryParamsKey: T.IGetQueryParams,
  ) => {
    const updateResponse = await api.updateIvrSession(ivrSessionId, updates)
    const userReadableError = 'Failed to update'
    const updateError = getUpdateErrorMessage(updateResponse, { userReadableError })

    if (updateError.message) {
      return setErrors({ ...errors, updateSearchIvrSessions: updateError })
    }

    // Update paginated search ivrSessions endpoint
    if (searchQueryParamsKey) {
      let searchKey = '/ivrSession/search'
      const queryParams = stringifyParams(['filter', 'populate'], searchQueryParamsKey)
      searchKey += `?${queryString.stringify(queryParams)}`
      await mutate(searchKey)
    }
  }

  return {
    ...response,
    ivrSession,
    update,
    updateSearch,
    error,
    errors,
  }
}
